/**
 * In-app review prompts, gated and fired at genuine delight moments.
 *
 * The OS owns the rating dialog: Apple's SKStoreReviewController / Google Play
 * In-App Review are throttled (Apple ~3 prompts/user/year) and may show nothing.
 * We therefore (a) call them only at high-signal moments, (b) self-gate so we
 * spend the budget on the best moments, and (c) track *prompted* — never
 * *rated*, because the OS never tells us whether the user actually rated.
 *
 * Each trigger fires at most once ever; across triggers we cap at `maxPrompts`
 * with a `cooldownDays` gap, so the first eligible milestone wins and a fast
 * user is prompted once, not five times.
 */
import { Capacitor } from '@capacitor/core';
import { safeGetJSON, safeSetJSON } from './storage';

export type ReviewTrigger =
  | 'first_quiz'
  | 'first_unit'
  | 'course_complete'
  | 'empathy_positive'
  | 'circle_connect';

export interface ReviewPromptState {
  lastPromptedAt: number | null;
  promptCount: number;
  firedTriggers: ReviewTrigger[];
}

const STORAGE_KEY = 'review_prompt_state';
const DAY_MS = 86_400_000;
const DEFAULT_COOLDOWN_DAYS = 120;
const DEFAULT_MAX_PROMPTS = 3;

const DEFAULT_STATE: ReviewPromptState = {
  lastPromptedAt: null,
  promptCount: 0,
  firedTriggers: [],
};

export function getReviewPromptState(): ReviewPromptState {
  // `?? DEFAULT_STATE` guards a literal stored `null` (corrupted storage), which
  // safeGetJSON would return as-is rather than the default.
  const stored =
    safeGetJSON<Partial<ReviewPromptState>>(STORAGE_KEY, DEFAULT_STATE) ?? DEFAULT_STATE;
  // Defensive: tolerate partial/legacy shapes.
  return {
    lastPromptedAt: stored.lastPromptedAt ?? null,
    promptCount: stored.promptCount ?? 0,
    firedTriggers: Array.isArray(stored.firedTriggers) ? stored.firedTriggers : [],
  };
}

/**
 * Pure decision: may we prompt for `trigger` given current state and time?
 * Exposed for unit testing without any Capacitor dependency.
 */
export function shouldPrompt(
  state: ReviewPromptState,
  trigger: ReviewTrigger,
  now: number,
  cooldownDays: number = DEFAULT_COOLDOWN_DAYS,
  maxPrompts: number = DEFAULT_MAX_PROMPTS,
): boolean {
  if (state.firedTriggers.includes(trigger)) return false;
  if (state.promptCount >= maxPrompts) return false;
  if (state.lastPromptedAt !== null && now - state.lastPromptedAt < cooldownDays * DAY_MS) {
    return false;
  }
  return true;
}

/**
 * Fire the native review dialog for `trigger` if the gate allows. No-op on web.
 * State only advances when `requestReview()` resolves without throwing.
 */
export async function maybeRequestReview(trigger: ReviewTrigger): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  const prev = getReviewPromptState();
  const now = Date.now();
  if (!shouldPrompt(prev, trigger, now)) return;

  // Reserve the slot SYNCHRONOUSLY, before any await. Triggers can fire in the
  // same tick (e.g. a domain-closing quiz enqueues both first_unit and
  // course_complete); writing first means a concurrent call reads the reserved
  // state and is blocked by the cooldown, so only one prompt fires per moment.
  safeSetJSON<ReviewPromptState>(STORAGE_KEY, {
    lastPromptedAt: now,
    promptCount: prev.promptCount + 1,
    firedTriggers: [...prev.firedTriggers, trigger],
  });

  try {
    const { InAppReview } = await import('@capacitor-community/in-app-review');
    await InAppReview.requestReview();
  } catch {
    // Plugin/throttle error: roll back the reservation so a later genuine
    // moment can still try (we never actually showed a dialog).
    safeSetJSON<ReviewPromptState>(STORAGE_KEY, prev);
  }
}
